import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";
import { Button } from "@/components/ui/button";

let deferred = null;
const listeners = new Set();

const notify = () => listeners.forEach((fn) => fn());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    notify();
  });
}

export const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true);

const isIOS = () =>
  typeof navigator !== "undefined" &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

/** Chrome / Android install prompt, with the Safari share-sheet route as a fallback. */
export const useInstallPrompt = () => {
  const [, bump] = useState(0);

  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);

  const install = async () => {
    if (!deferred) return false;
    deferred.prompt();
    const choice = await deferred.userChoice;
    deferred = null;
    notify();
    return choice?.outcome === "accepted";
  };

  return { canInstall: Boolean(deferred), install, installed: isStandalone(), ios: isIOS() };
};

const KEY = "chambers-install-dismissed";
const SNOOZE = "chambers-install-snooze";
const WEEK = 7 * 24 * 60 * 60 * 1000;

const isHidden = () => {
  if (localStorage.getItem(KEY) === "1") return true;
  const until = Number(localStorage.getItem(SNOOZE) || 0);
  return until > Date.now();
};

/** Settings can bring the how-to back at any time. */
export const showInstallHelp = () => {
  localStorage.removeItem(KEY);
  localStorage.removeItem(SNOOZE);
  notify();
};

export const InstallPrompt = () => {
  const { canInstall, install, installed, ios } = useInstallPrompt();
  const [hidden, setHidden] = useState(isHidden);

  useEffect(() => {
    const fn = () => setHidden(isHidden());
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);

  const later = () => {
    localStorage.setItem(SNOOZE, String(Date.now() + WEEK));
    setHidden(true);
  };

  const never = () => {
    localStorage.setItem(KEY, "1");
    setHidden(true);
  };

  if (installed || hidden) return null;
  if (!canInstall && !ios) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:w-[26rem] z-50 surface p-5 shadow-[0_18px_40px_rgba(15,15,15,0.28)] print:hidden"
    >
      <button
        data-testid="install-dismiss"
        onClick={later}
        aria-label="Not now"
        className="absolute top-3 right-3 h-8 w-8 grid place-items-center rounded-sm hover:bg-accent transition-colors duration-200"
      >
        <X className="h-4 w-4" />
      </button>
      <p className="label-caps">Keep the diary one tap away</p>

      {canInstall ? (
        <p className="mt-2 text-sm text-muted-foreground pr-6">
          Install it and the diary opens full-screen from your home screen, working with or without a
          signal.
        </p>
      ) : (
        <>
          <p className="mt-2 text-sm text-muted-foreground pr-6">
            Add it to your iPad's home screen and it opens full-screen, straight into today, with or
            without a signal.
          </p>
          <ol
            className="mt-3 space-y-1.5 text-sm list-decimal pl-5"
            data-testid="install-ios-steps"
          >
            <li>
              In Safari's toolbar, tap <Share className="inline h-4 w-4 -mt-0.5" />{" "}
              <strong>Share</strong>
            </li>
            <li>
              Scroll and choose <strong>Add to Home Screen</strong>
            </li>
            <li>
              Tap <strong>Add</strong> — look for the chambers mark on your home screen
            </li>
          </ol>
        </>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {canInstall ? (
          <Button
            data-testid="install-now-btn"
            onClick={async () => {
              const ok = await install();
              if (ok) never();
            }}
            className="rounded-full h-11 px-5"
          >
            <Download className="h-4 w-4 mr-2" /> Install the diary
          </Button>
        ) : (
          <Button
            data-testid="install-done-btn"
            onClick={never}
            className="rounded-full h-11 px-5"
          >
            Done, it's on my home screen
          </Button>
        )}
        <button
          data-testid="install-later-btn"
          onClick={later}
          className="text-sm font-medium text-muted-foreground hover:text-foreground underline transition-colors duration-200"
        >
          Remind me next week
        </button>
      </div>
    </div>
  );
};
